import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './styles/ServicesSection.css';

// Import service icons
import managedservices from '../../../assets/images/services/manages-services-logo.png'
import infrastructureServices from '../../../assets/images/services/infrastructure-services-logo.png'
import strategyIcon from '../../../assets/images/services/ITConsultingProfessionalServices.png';
import recruiterIcon from '../../../assets/images/services/talent services.png';

const ServicesSection = () => {
  const containerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const navigate = useNavigate();
  const services = [
    {
      code: 'MS \u00B7 01',
      title: "Managed Services",
      description: "Proactive, ongoing IT management that keeps your systems secure, monitored, and optimized 24/7. We handle daily operations, support, and maintenance so your team can focus on core business goals.",
      icon: managedservices,
      link: "/services/Managed-Services",
    },
    {
      code: 'IS \u00B7 02',
      title: "Infrastructure Services",
      description: "Design, deployment, and modernization of secure, scalable IT infrastructure. From networks and data centers to cloud and colocation, we build reliable foundations that support performance and growth.",
      icon: infrastructureServices,
      link: "/services/Infrastructure-Services",
    },
    {
      code: 'CS \u00B7 03',
      title: "IT Consulting & Professional Services",
      description: "Empowering businesses with expert IT solutions and professional services to drive innovation, efficiency, and growth.",
      icon: strategyIcon,
      link: "/services/IT-Consulting-Professional-Services",
    },
    {
      code: 'TS \u00B7 04',
      title: "Talent Services",
      description: "Connecting businesses with top talent to build strong, skilled, and future ready teams.",
      icon: recruiterIcon,
      link: "/services/Talent-Services ",
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const scrollHeight = container.scrollHeight - window.innerHeight;

      // Check if section is in view
      if (rect.top <= 0 && rect.bottom >= window.innerHeight) {
        // Section is "stuck" - calculate horizontal scroll progress
        const progress = Math.abs(rect.top) / scrollHeight;
        setScrollProgress(Math.min(Math.max(progress, 0), 1));
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate horizontal translation
  const maxScroll = (services.length * 500) - window.innerWidth + 200;
  const translateX = -scrollProgress * maxScroll;

  // Which card is currently centered, for the readout next to the progress bar
  const activeIndex = Math.min(
    services.length - 1,
    Math.floor(scrollProgress * services.length)
  );

  return (
    <div ref={containerRef} className="services-wrapper">
      <div className="services-sticky-container">
        <div className="services-grid-bg" aria-hidden="true"></div>

        <div className="services-header">
          <div className="services-eyebrow">
            <span className="services-live-dot" aria-hidden="true"></span>
            Service catalog
          </div>
          <p className="services-subtitle">Comprehensive IT solutions for your business</p>
        </div>

        <div className="services-track-wrapper">
          <div
            className="services-track"
            style={
              window.innerWidth > 768
                ? { transform: `translateX(${translateX}px)` }
                : {}
            }          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <div className="service-card-head">
                  <span className="service-code">{service.code}</span>
                </div>

                <div className="service-icon-frame">
                  <img src={service.icon} alt={service.title} className="service-icon" />
                  <span className="scan-corner tl" aria-hidden="true" />
                  <span className="scan-corner tr" aria-hidden="true" />
                  <span className="scan-corner bl" aria-hidden="true" />
                  <span className="scan-corner br" aria-hidden="true" />
                </div>

                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>

                <button
                  className="service-btn"
                  onClick={() => navigate(service.link)}
                >
                  Learn more
                  <svg
                    className="service-btn-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </button>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Scroll Progress Indicator */}
        <div className="scroll-indicator-wrap">
          <div className="scroll-indicator">
            <div
              className="scroll-progress-bar"
              style={{ width: `${scrollProgress * 100}%` }}
            />
          </div>
          <span className="scroll-indicator-count">
            {String(activeIndex + 1).padStart(2, '0')} / {String(services.length).padStart(2, '0')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ServicesSection;
